import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ChangePrice } from "../components/fleet/ChangePrice";
import { CreatePrice } from "../components/fleet/CreatePrice";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { EmptyState, ErrorNotice, Notice, SkeletonRows } from "../components/ui/Feedback";
import { Cell, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import type { Column } from "../components/ui/Table";
import { Metric } from "../components/ui/Text";
import { get } from "../lib/api";
import { count } from "../lib/format";
import { type PriceLineage, groupPrices } from "../lib/priceLineage";
import { useSession } from "../lib/session";
import { money, perKwh } from "../lib/tariff";
import type { TariffListing } from "../lib/types";

/**
 * Prices — what a driver pays at a public station. pay-ui spec §6.1.
 *
 * ## Why this page exists
 *
 * Until the server grew `POST /tariffs`, a new price was an `INSERT` somebody typed
 * against the live database: a commercial decision with no validation, no audit
 * entry, and no way for a second person to see it. This is the same gap the depot
 * and provisioning pages closed, and a worse one, because a mistyped price is money
 * that has already been taken off a driver.
 *
 * ## A row here is a price, not a row in the database
 *
 * `GET /tariffs` serves every *version* ever minted, because each one is what some
 * order was quoted at. Rendering them flat would show one price four times and leave
 * the operator to work out which figure is in force, so `lib/priceLineage.ts` folds
 * them into prices and this page shows the version in force with its past figures
 * underneath.
 *
 * ## Changing a price moves the stations on it
 *
 * "Change" does not edit a row. The server mints a new version, retires the current
 * one, and moves every subscribed station onto the new version in one transaction —
 * which is why the subscriber count is on this table rather than being left for the
 * fleet page: it is the blast radius of the button beside it, and the change form
 * states it again before anything is written.
 *
 * Orders already placed keep pointing at the version they were quoted at. That is the
 * whole reason for the indirection, and it is why the history below a price is worth
 * keeping on screen: those rows are still what somebody's receipt refers to.
 *
 * ## Retiring a price is still not offered
 *
 * Stopping a price without a successor has to answer where its stations go, and
 * nothing in the API answers that yet. A button that silently left them on a price
 * nobody may sell at would be the wrong answer shipped quietly.
 */

const COLUMNS: readonly Column[] = [
  { key: "name", label: "Name", width: "24%" },
  { key: "rate", label: "Per kWh", width: "14%" },
  { key: "range", label: "Order range", width: "18%" },
  { key: "presets", label: "One-tap buttons", width: "20%" },
  { key: "stations", label: "Stations", numeric: true, width: "8%" },
  { key: "status", label: "Status" },
];

export function Prices() {
  const { can } = useSession();
  const [isCreating, setIsCreating] = useState(false);
  /** The lineage whose change form is open, if any. One at a time, by design. */
  const [changing, setChanging] = useState<string | undefined>(undefined);
  /** The lineage whose past versions are on screen. */
  const [expanded, setExpanded] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const prices = useQuery({
    // The same key the station sale panel's picker uses, so a price created here
    // is in that dropdown without a reload.
    queryKey: ["tariffs"],
    queryFn: () => get<readonly TariffListing[]>("/tariffs"),
  });

  const versions = prices.data ?? [];
  const grouped = groupPrices(versions);
  const sellable = grouped.filter((price) => price.current.active);
  const editing = grouped.find((price) => price.lineageId === changing);

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Prices"
        metrics={
          <>
            <Metric label="Sellable" value={count(sellable.length)} hint="active prices" />
            <Metric
              label="Currencies"
              value={count(new Set(sellable.map((price) => price.current.currency)).size)}
            />
            <Metric
              label="Versions"
              value={count(versions.length)}
              hint="every figure ever quoted"
              tone={versions.length > grouped.length ? "signal" : "muted"}
            />
          </>
        }
        lede="What a driver is quoted when they scan a station's sticker. Changing a price mints a new version of it and moves every station on it across; the version an order was quoted at is kept, because it is the record of what somebody was charged."
        actions={
          can("tariffs.create") && !isCreating && editing === undefined ? (
            <Button variant="primary" onClick={() => setIsCreating(true)}>
              New price
            </Button>
          ) : undefined
        }
      />

      {notice === undefined ? null : <Notice tone="ok">{notice}</Notice>}

      {isCreating ? (
        <CreatePrice
          onCreated={(price) => {
            setNotice(
              `${price.name} created at ${perKwh(price.priceMinorPerKwh, price.currency)}. Put a station on it from Charge points.`,
            );
            setIsCreating(false);
          }}
          onCancel={() => setIsCreating(false)}
        />
      ) : null}

      {editing === undefined ? null : (
        <ChangePrice
          price={editing}
          onChanged={(change) => {
            setNotice(
              change.rotated
                ? `${change.tariff.name} is now ${perKwh(change.tariff.priceMinorPerKwh, change.tariff.currency)}. ${stationsMoved(change.repointedChargePoints)}`
                : `${change.tariff.name} renamed. No new version was minted and no station moved.`,
            );
            setChanging(undefined);
          }}
          onCancel={() => setChanging(undefined)}
        />
      )}

      {prices.isError ? (
        <ErrorNotice error={prices.error} onRetry={() => void prices.refetch()} />
      ) : null}

      <TableFrame>
        <TableHead columns={COLUMNS} />
        {prices.isPending ? (
          <SkeletonRows rows={3} columns={COLUMNS.length} />
        ) : (
          <tbody>
            {grouped.map((price, index) => (
              <PriceRows
                key={price.lineageId}
                price={price}
                index={index}
                isExpanded={expanded === price.lineageId}
                onToggle={() =>
                  setExpanded((current) =>
                    current === price.lineageId ? undefined : price.lineageId,
                  )
                }
                onChange={
                  can("tariffs.update") && price.current.active
                    ? () => {
                        setIsCreating(false);
                        setNotice(undefined);
                        setChanging(price.lineageId);
                      }
                    : undefined
                }
              />
            ))}
          </tbody>
        )}
      </TableFrame>

      {!prices.isPending && grouped.length === 0 ? (
        <EmptyState
          title="No prices yet."
          detail="No station can be put on public sale until one exists: a station on sale must have a price, and the database refuses the pair any other way."
          action={
            can("tariffs.create") && !isCreating ? (
              <Button variant="primary" onClick={() => setIsCreating(true)}>
                Create the first price
              </Button>
            ) : undefined
          }
        />
      ) : null}

      <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
        A price is never edited in place and never deleted: changing one mints a version and retires
        the last, so every order keeps the figure it was quoted. Retiring a price outright is not
        possible here yet — it has to answer where its stations go. A station is put on a price from
        its own page under{" "}
        <Link
          to="/fleet"
          className="text-signal underline decoration-1 underline-offset-4 hover:no-underline"
        >
          Charge points
        </Link>
        .
      </p>
    </>
  );
}

/**
 * One price: the version in force, and — when opened — the versions behind it.
 *
 * The past versions are rows in the same table rather than a nested one, so the
 * figures line up under the columns they belong to. That is the entire reason a
 * fragment of two element types is worth the indirection here: an operator comparing
 * "what is it now" with "what was it in March" is comparing down a column.
 */
function PriceRows({
  price,
  index,
  isExpanded,
  onToggle,
  onChange,
}: {
  readonly price: PriceLineage;
  readonly index: number;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
  /** Absent when the caller may not write, or the price has no sellable version. */
  readonly onChange: (() => void) | undefined;
}) {
  const current = price.current;

  return (
    <>
      <TableRow index={index}>
        <Cell>
          <div className="flex flex-col gap-1">
            <span className="font-medium text-ink">{current.name}</span>
            {price.history.length === 0 ? null : (
              <button
                type="button"
                onClick={onToggle}
                aria-expanded={isExpanded}
                className="self-start text-micro uppercase tracking-wide text-ink-faint underline decoration-1 underline-offset-4 hover:text-ink hover:no-underline"
              >
                {isExpanded ? "Hide" : "Show"} {versionsBefore(price.history.length)}
              </button>
            )}
          </div>
        </Cell>
        <Cell>{perKwh(current.priceMinorPerKwh, current.currency)}</Cell>
        <Cell muted>
          {money(current.minAmountMinor, current.currency)} –{" "}
          {money(current.maxAmountMinor, current.currency)}
        </Cell>
        {/*
          Spelled out rather than counted: three buttons at the wrong
          amounts is the mistake worth seeing, and "3 presets" hides it.
        */}
        <Cell muted>
          {current.presetAmountsMinor.length === 0
            ? "None"
            : current.presetAmountsMinor.map((a) => money(a, current.currency)).join(" · ")}
        </Cell>
        {/*
          The blast radius of the button in the next cell. Counted across the price's
          versions, because a station left on an older one is still selling this price.
        */}
        <Cell numeric muted={price.subscriberCount === 0}>
          {price.subscriberCount}
        </Cell>
        <Cell muted={current.active}>
          <div className="flex items-center justify-between gap-3">
            <span>{current.active ? "Sellable" : "Retired — stations may still be on it"}</span>
            {onChange === undefined ? null : (
              <Button variant="ghost" onClick={onChange}>
                Change
              </Button>
            )}
          </div>
        </Cell>
      </TableRow>

      {isExpanded
        ? price.history.map((version) => (
            <SupersededRow key={version.id} version={version} index={index} />
          ))
        : null}
    </>
  );
}

/** A version that has been replaced. Still what some order's `tariff_id` names. */
function SupersededRow({
  version,
  index,
}: {
  readonly version: TariffListing;
  readonly index: number;
}) {
  return (
    <TableRow index={index}>
      <Cell muted>
        <span className="pl-4">Until {onDay(version.createdAt)}</span>
      </Cell>
      <Cell muted>{perKwh(version.priceMinorPerKwh, version.currency)}</Cell>
      <Cell muted>
        {money(version.minAmountMinor, version.currency)} –{" "}
        {money(version.maxAmountMinor, version.currency)}
      </Cell>
      <Cell muted>
        {version.presetAmountsMinor.length === 0
          ? "None"
          : version.presetAmountsMinor.map((a) => money(a, version.currency)).join(" · ")}
      </Cell>
      {/*
        Non-zero here is worth seeing rather than hiding: it is a station left on a
        superseded version, which the next change to this price will collect.
      */}
      <Cell numeric muted={version.subscriberCount === 0}>
        {version.subscriberCount}
      </Cell>
      <Cell muted>Superseded</Cell>
    </TableRow>
  );
}

/**
 * The day a version came into force.
 *
 * The date and not the time: the question a superseded row answers is "which figure
 * was in force when this order was placed", and a minute stamp on a price change reads
 * as more precision than the answer has.
 */
function onDay(iso: string): string {
  const at = new Date(iso);

  return Number.isNaN(at.getTime())
    ? iso
    : at.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function versionsBefore(historyLength: number): string {
  return historyLength === 1 ? "1 earlier version" : `${historyLength} earlier versions`;
}

/** What the server actually moved, which is not always what the form predicted. */
function stationsMoved(repointed: number): string {
  if (repointed === 0) return "No station was on it.";

  return repointed === 1 ? "1 station moved across." : `${repointed} stations moved across.`;
}
